import { DatePipe } from "@angular/common"
import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core"
import { VoiceCall, VoiceCallTest } from "dialer"
import { extractMacAndHci } from "dialer"
import { BsModalService } from "ngx-bootstrap/modal"
import { Subscription } from "rxjs"
import { debounceTime } from "rxjs/operators"
import { AppState } from "../../core/app-state"
import { NumericCompareFn, SortCompareFn } from "../../shared/components/sort-field/sort-field.component"
import { StatGroup, StatGroupResult, StatMetric, TableViewComponentBase, TableViewContext, TableViewQueryRequest, TableViewQueryResponse } from "../../shared/components/table-view/table-view-component-base"
import { getMatcherForPattern } from "../../shared/directives/filter-field.directive"
import { groupBy, map } from 'lodash';

class VoiceCallTestRow {

  //test level
  testId: string
  testGroup: number
  testStartTime: string
  testStatus: string
  dialedNumber: string
  callSeqPosition: number // (1st or second call... 1 or 2)
  testResultMsg: string

  // phone level
  phoneName: string
  phoneMac: string
  networkRegName: string
  networkRegStrength: number

  btHci: string

  // network level

  // call level
  call: VoiceCall
  callState: string
  callStartTime: string
  callDuration: number
  callDisconnectReason: string
  callDialingStateStart: string
  callDialingStateDuration: number
  callAlertingStateStart: string
  callAlertingStateDuration: number
  callActiveStateStart: string
  callActiveStateDuration: number
  callDisconnectedStateStart: string
  callDisconnectedStateDuration: number
  callRemovedStateStart: string
  callRemovedStateDuration: number
  callSetupDuration?: number;
}


@Component({
    selector: 'fes-dialer-client-result-calls-table',
    templateUrl: './dialer-client-result-calls-table.component.html',
    styleUrls: [ './dialer-client-result-calls-table.component.scss',
      '../../shared/components/table-view/scss/table-view.scss' ]
  })
  export class DialerClientResultCallsTableComponent extends TableViewComponentBase<VoiceCallTestRow> implements OnInit, OnDestroy {

    @Input('callTests') set callTestsSetter(callTests: VoiceCallTest[]){
      this.allCallTestRows = this.callTestsToRows(callTests)
      this.load()
    }

    allCallTestRows: VoiceCallTestRow[]
    callTests: VoiceCallTest[] = []
    keyUpSubscription : Subscription
    allExpanded = false

    constructor(
        appState: AppState,
        elementRef: ElementRef,
        datePipe: DatePipe,
        bsModalService: BsModalService
    ){
      super(appState,bsModalService,elementRef,datePipe)
      this.pagingEnabled = false
      this.pageSize = 0

      this.tableColumns  = [
        { title: 'Phone Name', name: 'phoneName', visible: true},
        { title: 'Phone MAC', name: 'phoneMac', visible: true},
        { title: 'HCI', name: 'btHci', visible: true},
        { title: 'Network', name: 'networkRegName', visible: true},
        { title: 'Signal', name: 'networkRegStrength', visible: true, type: 'percent'},
        { title: 'Call#', name: 'callSeqPosition', visible: true, type: 'numeric'},
        { title: 'Dialed #', name: 'dialedNumber', visible: true},
        { title: 'Call State', name: 'callState', visible: false},
        { title: 'Call Start Time', name: 'callStartTime', visible: true},
        { title: 'Duration', name: 'callDuration', visible: true, type: 'numeric'},
        { title: 'Test Group', name: 'testGroup', visible: false},
        { title: 'Test Start', name: 'testStartTime', visible: false},
        { title: 'Test Status', name: 'testStatus', visible: false},
        { title: 'Test Summary', name: 'testResultMsg', visible: false},
        { title: 'Setup', name: 'callSetupDuration', visible: true, type: 'numeric'},
        { title: 'Dialing Event', name: 'callDialingStateStart', visible: false},
        { title: 'Dialing', name: 'callDialingStateDuration', visible: true, type: 'numeric'},
        { title: 'Alerting Event', name: 'callAlertingStateStart', visible: false},
        { title: 'Alerting', name: 'callAlertingStateDuration', visible: true, type: 'numeric'},
        { title: 'Active Event', name: 'callActiveStateStart', visible: false},
        { title: 'Active Duration', name: 'callActiveStateDuration', visible: false, type: 'numeric'},
        { title: 'Disconnected Event', name: 'callDisconnectedStateStart', visible: false},
        { title: 'Disconnected Duration', name: 'callDisconnectedStateDuration', visible: false, type: 'numeric'},
        { title: 'Removed Event', name: 'callRemovedStateStart', visible: false},
        { title: 'Removed Duration', name: 'callRemovedStateDuration', visible: false, type: 'numeric'},
        { title: 'Disconnect', name: 'callDisconnectReason', visible: true},
      ]

      //this.bShowFilterOperator = true

    }


    ngOnInit(): void {
      this.keyUpSubscription = this.filterKeyUp$.pipe(debounceTime(500)).subscribe( () => this.load() )
      this.init()
      this.setContext({} as TableViewContext)
    }


    ngOnDestroy(): void {
      this.keyUpSubscription?.unsubscribe()
      super.destroy()
    }


    callTestsToRows = (callTests: VoiceCallTest[]) => {
      return callTests?.map( callTest => {
        const row = new VoiceCallTestRow()

        // test level
        row.testId = callTest.id
        row.testGroup = callTest.groupNumber
        row.testStartTime = callTest.testStartTime
        row.callSeqPosition = callTest.index + 1
        row.dialedNumber = callTest.dialNumber
        row.testStatus = callTest.status
        row.testResultMsg = callTest.reason

        // phone level
        row.phoneMac = callTest.phoneMacAddress
        const phone = callTest.phone
        if ( phone ) {
          row.phoneName = callTest.phone?.modemProps?.name?.trim()
          const reg = phone.network?.registration
          if ( reg ) {
            row.networkRegName = reg.name?.trim()
            row.networkRegStrength = Number(reg.strength)
          }
        }

        // call level
        const call = callTest.call
        if ( call ) {
          row.call = call
          row.btHci = extractMacAndHci(call.modemPath)?.hci;
          row.callDisconnectReason = call.disconnectReason
          row.callStartTime = call.props.startTime
          row.callState = call.state
          row.callDuration = call.duration
          if ( call.transitions?.length ) {
            const transitions = call.transitions
            const dialTrans = transitions.find(t=>t.state=='DIALING')
            if ( dialTrans ) {
              row.callDialingStateStart = dialTrans.time
              row.callDialingStateDuration = dialTrans.duration
            }
            const alertingTrans = transitions.find(t=>t.state=='ALERTING')
            if ( alertingTrans ) {
              row.callAlertingStateStart = alertingTrans.time
              row.callAlertingStateDuration = alertingTrans.duration
            }
            const activeTrans = transitions.find(t=>t.state=='ACTIVE')
            if ( activeTrans ) {
              row.callActiveStateStart = activeTrans.time
              row.callActiveStateDuration = activeTrans.duration
            }
            const disconnectTrans = transitions.find(t=>t.state=='DISCONNECTED')
            if ( disconnectTrans ) {
              row.callDisconnectedStateStart = disconnectTrans.time
              row.callDisconnectedStateDuration = disconnectTrans.duration
            }
            const removedTrans = transitions.find(t=>t.state=='REMOVED')
            if ( removedTrans ) {
              row.callRemovedStateStart = removedTrans.time
              row.callRemovedStateDuration = removedTrans.duration
            }
            if ( row.callDialingStateStart != null || row.callAlertingStateStart != null ) {
              row.callSetupDuration =
                (row.callDialingStateStart ? row.callDialingStateDuration : 0)
                + (row.callAlertingStateStart ? row.callAlertingStateDuration : 0)
            }
          }
        }

        return row
      })
    }


    // override
    async doQuery(queryRequest: TableViewQueryRequest) {
      const requestId = queryRequest.requestId

      const fieldFilters = this.buildPopulatedFilters()
      const filterRow = ( row: VoiceCallTestRow ) => {
        for ( const fieldFilter of fieldFilters) {
          if ( fieldFilter.value ) {
            const value = row[fieldFilter.key]
            const bNumeric = ['numeric','percent'].includes(this.columnByName[fieldFilter.key].type)
            if ( bNumeric ) {
              if ( fieldFilter.mode == '>=' ) {
                if ( value < Number(fieldFilter.value) ) {
                  return false
                } else {
                  continue
                }
              } else if (fieldFilter.mode == '<') {
                if ( value >= Number(fieldFilter.value) ) {
                  return false
                } else {
                  continue
                }
              }
            }
            const matcherFn = getMatcherForPattern(fieldFilter.value,fieldFilter.mode)
            if ( !matcherFn(`${value==null?'':value}`) ) {
              return false
            }
          }
        }
        return true
      }

      this.allCallTestRows.forEach(row=>{
        row['OS'] = row.phoneName?.trim() ?
          row.phoneName?.toLowerCase().indexOf('iphone') >= 0 ? 'iOS' : 'Android'
          : 'Unknown'
      })

      this.records = this.allCallTestRows.filter( filterRow )

      if ( this.sortColumns?.length ) {
        const sortFns = this.sortColumns?.map(sortCol=> {
          const key = sortCol.key
          const tableColumn = this.tableColumns.find(tc=>tc.name==key)
          const ascSortFn = ['numeric','percent'].includes(tableColumn.type) ?
            (a,b)=>NumericCompareFn(a[key],b[key]) :
            (a,b)=>SortCompareFn(a[key],b[key])
          const sortFn = sortCol.order == 'ASC' ? ascSortFn : (a,b) => (ascSortFn(a,b)*-1) as 0|1|-1
          return sortFn
        })
        const compositSortFn = (a,b) => {
          for( const sortFn of sortFns ) {
            const rtn = sortFn(a,b)
            if ( rtn ) {
              return rtn
            }
          }
          return 0
        }
        this.records.sort( (a,b) => compositSortFn(a,b) )
      }

      return {
        requestId,
        tableRowCount: this.allCallTestRows.length,
        queryRowCount: this.records.length,
        statGroups: this.buildStatGroupResults(),
        rows: this.records,
        pageNumber: 1,
        selectedIndices: []
      }
    }

    generateStatRows(data: VoiceCallTestRow[], statGroup: StatGroup): any[] {
      const grouped = groupBy(data, row =>
        statGroup.groupBy.map(key => row[key as keyof VoiceCallTestRow]).join('|')
      );

      return map(grouped, (groupRows, groupKey) => {
        const row: {[key:string]:any} = {};
        const groupValues = groupKey.split('|');
        statGroup.groupBy.forEach((field, i) => {
          row[field] = groupValues[i];
        });

        statGroup.metrics.forEach(metric => {
          metric.statTypes.forEach(statType => {
            const fieldName = `${metric.column}_${statType}`;

            if (metric.column === '*' && statType === 'count') {
              row[fieldName] = groupRows.length;
              return;
            }

            const values = groupRows.map(r => r[metric.column as keyof VoiceCallTestRow]);

            if (metric.format?.dataType === 'date') {
              const validDates = values.filter(v => v && (v as string).trim() !== '');
              switch(statType) {
                case 'min':
                  row[fieldName] = validDates.reduce((min, curr) =>
                    !min || new Date(curr as string).getTime() < new Date(min as string).getTime() ? curr : min
                  );
                  break;
                case 'max':
                  row[fieldName] = validDates.reduce((max, curr) =>
                    !max || new Date(curr as string).getTime() > new Date(max as string).getTime() ? curr : max
                  );
                  break;
              }
            } else {
              const validNumbers = values.filter(v => typeof v === 'number' && !isNaN(v)) as number[];
              if (validNumbers.length === 0) {
                row[fieldName] = null;
              } else {
                switch(statType) {
                  case 'min':
                    row[fieldName] = Math.min(...validNumbers);
                    break;
                  case 'max':
                    row[fieldName] = Math.max(...validNumbers);
                    break;
                  case 'avg':
                    row[fieldName] = validNumbers.reduce((a, b) => a + b, 0) / validNumbers.length;
                    break;
                }
              }
            }
          });
        });
        return row;
      });
     }

    buildStatGroupResults(): StatGroupResult[] {
      const metrics: StatMetric[] = [
        {
          column: 'callDuration',
          title: 'Call Duration',
          hidden: false,
          statTypes: ['min', 'max', 'avg'],
        },
        {
          column: 'callSetupDuration',
          title: 'Setup',
          hidden: false,
          statTypes: ['min', 'max', 'avg'],
        },
        {
          column: 'callDialingStateDuration',
          title: 'Dialing',
          hidden: false,
          statTypes: ['min', 'max', 'avg'],
        },
        {
          column: 'callAlertingStateDuration',
          title: 'Alerting',
          hidden: false,
          statTypes: ['min', 'max', 'avg'],
        },
        {
          column: 'callStartTime',
          title: 'Call Start Time',
          hidden: false,
          format: {
            dataType: 'date'
          },
          statTypes: ['min', 'max'],
        },
        {
          column: '*',
          format: { dataType: 'number', layout: '1.0' },
          statTypes: ['count']
        },
      ];
      const statGroups: StatGroup[] = [
        {
          id: 'byNetwork',
          title: 'Network',
          visible: true,
          groupBy: ['networkRegName'],
          metrics,
        },
        {
          id: 'byPhoneType',
          title: 'Phone OS',
          visible: true,
          groupBy: ['OS'],
          metrics,
        },
        {
          id: 'byPhone',
          title: 'Phone',
          visible: false,
          groupBy: ['phoneMac','phoneName'],
          metrics,
        },
      ];
      const resp = statGroups.map(statGroup=> ({
        ...statGroup,
        rows: this.generateStatRows(this.records, statGroup)
      }));
      // console.log(JSON.stringify(resp));
      return resp;
    }

    trackByTestId(index:number, row: VoiceCallTestRow) {
      return row.testId
    }

    expanded = {}

    toggleExpanded( row: VoiceCallTestRow ) {
      this.expanded[row.testId] = !this.expanded[row.testId]
      this.allExpanded = this.areAllVisibleExpanded()
    }

    toggleAllVisibleExpanded() {
      //console.log(JSON.stringify(this.expanded))
      this.allExpanded = !this.areAllVisibleExpanded()
      this.expanded={}
      if ( this.allExpanded) {
        this.allCallTestRows?.forEach(row=> this.expanded[row.testId] = true)
      }
    }

    areAllVisibleExpanded() {
      return !this.records || this.records.every(row=> row ? this.expanded[row.testId] === true : false )
    }

    reload(){
      if ( this.filtersModified() ) {
        this.load()
      }
    }

  }